import { useCallback, useMemo, useRef } from "react";
import {
  type BuyerQualificationReviewResponsePatchDto,
  type BuyerBidDetailDto,
  useUpdateBuyerQualificationReviewMutation,
} from "../../../autogen/bff-api";
import { useCreateEditor, useEditor } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

interface EditorState extends Record<string, unknown> {
  verdicts: Record<string, boolean | null>;
  comments: Record<string, string>;
}

type EditorFields = Partial<EditorState> & { forDeliverySubmittedAt?: string };

interface UseEditor {
  state: EditorState;
  setVerdict: (criterionKey: string, value: boolean | null) => void;
  setComment: (criterionKey: string, value: string) => void;
  isSaving: boolean;
}

export const useBuyerQualificationReviewEditor = (
  eventId: string,
  bidId: string,
  detail: BuyerBidDetailDto,
): UseEditor => {
  const { trigger } = useApiError();
  const [update] = useUpdateBuyerQualificationReviewMutation();
  const entityId = `bid-qualification-review:${bidId}`;
  const detailRef = useRef(detail);
  detailRef.current = detail;

  const responses = detail.bid.qualificationResponses;
  const initialState = useMemo(() => toEditorState(responses), [responses]);

  const save = useCallback(
    async (fields: EditorFields): Promise<EditorState> => {
      const updated = await update({
        eventId,
        bidId,
        buyerQualificationReviewPatchDto: {
          responses: toPatchResponses(fields),
          forDeliverySubmittedAt: fields.forDeliverySubmittedAt,
        },
      }).unwrap();
      return toEditorState(updated.bid.qualificationResponses);
    },
    [bidId, eventId, update],
  );

  useCreateEditor<EditorState, EditorFields>(entityId, { initialState, save, onError: trigger });
  const { state, updateField, entityStatus } = useEditor<EditorState, EditorFields>(entityId);

  const setVerdict = useCallback(
    (criterionKey: string, value: boolean | null) =>
      updateField(
        { verdicts: { [criterionKey]: value }, forDeliverySubmittedAt: detailRef.current.bid.submittedAt ?? undefined },
        { debounceMs: DEBOUNCE_MS },
      ),
    [updateField],
  );

  const setComment = useCallback(
    (criterionKey: string, value: string) =>
      updateField(
        { comments: { [criterionKey]: value }, forDeliverySubmittedAt: detailRef.current.bid.submittedAt ?? undefined },
        { debounceMs: DEBOUNCE_MS },
      ),
    [updateField],
  );

  return { state, setVerdict, setComment, isSaving: entityStatus === "saving" };
};

const toEditorState = (responses: BuyerBidDetailDto["bid"]["qualificationResponses"]): EditorState => {
  const verdicts: Record<string, boolean | null> = {};
  const comments: Record<string, string> = {};
  for (const r of responses ?? []) {
    verdicts[r.criterionKey] = r.buyerVerdict ?? null;
    comments[r.criterionKey] = r.buyerComment ?? "";
  }
  return { verdicts, comments };
};

const toPatchResponses = (fields: EditorFields): BuyerQualificationReviewResponsePatchDto[] => {
  const byKey = new Map<string, BuyerQualificationReviewResponsePatchDto>();
  const responseFor = (criterionKey: string) => byKey.get(criterionKey) ?? { criterionKey };
  for (const [criterionKey, buyerVerdict] of Object.entries(fields.verdicts ?? {})) {
    byKey.set(criterionKey, { ...responseFor(criterionKey), buyerVerdict });
  }
  for (const [criterionKey, buyerComment] of Object.entries(fields.comments ?? {})) {
    byKey.set(criterionKey, { ...responseFor(criterionKey), buyerComment });
  }
  return Array.from(byKey.values());
};
